import { useToken } from "@chakra-ui/react";
import { PanInfo } from "framer-motion";
import { useRef, useState } from "react";

import { getOpenMap } from "../../../store/selectors";
import { usePublicationStore } from "../../../store/store";
import {
  findDropIndex,
  getMapElementAtPoint,
  getSelectedLayerIds,
  insertBlockAt,
} from "../utils/drag-utils";
import {
  FolderOpenById,
  Placement,
  buildFlatOrder,
  buildRenderRows,
  hasSameOrder,
  idsToPlacements,
  rebuildLayerTree,
} from "../utils/reorder-utils";

type DragEvent = MouseEvent | TouchEvent | PointerEvent;

/** The drag in progress. Kept in a ref since nothing is rendered from it. */
type DragSession = {
  /** The dragged row, or all selected layers if the dragged row is part of a multi-selection. */
  dragIds: string[];
  /** The flat order of all other rows. Only set when dragging multiple rows (see `handleDrag`). */
  otherIds: string[] | null;
};

/** The new order while dragging. Only written to the store on drop (see `handleDragEnd`). */
type Draft = { placements: Placement[]; folderOpenById: FolderOpenById };

/**
 * Drag and drop for the layer list. Dropping on another map in the map list moves the dragged
 * layers to that map, dropping anywhere else reorders them (also in or out of folders).
 *
 * A single dragged row is reordered by framer-motion (`handleReorder`). Multiple selected rows are
 * dragged as a block instead, which framer-motion can't do, so `handleDrag` positions them from
 * the pointer.
 *
 * While dragging, the new order only lives in `draft` and is shown by rebuilding the layers from
 * it (`effectiveLayers`). It's committed to the store on drop, so the rest of the app (e.g. the
 * map) doesn't re-render on every pointer move.
 */
export function useLayerDrag() {
  const { id: openMapId, layers } = usePublicationStore(getOpenMap);
  const reorderFlatLayers = usePublicationStore((state) => state.reorderFlatLayers);
  const moveLayersToMap = usePublicationStore((state) => state.moveLayersToMap);
  const [dragOverColor] = useToken("colors", ["brand.clear_creek"]);

  const [draft, setDraft] = useState<Draft | null>(null);
  // While a folder is dragged all folders are shown closed, so it can't get dropped in the middle
  // of another open folder's layers.
  const [isDraggingFolder, setIsDraggingFolder] = useState(false);

  const sessionRef = useRef<DragSession | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // The map row currently highlighted as drop target.
  const highlightedMapRef = useRef<HTMLElement | null>(null);

  const shownLayers = isDraggingFolder
    ? layers.map((item) => (item.type === "folder" ? { ...item, opened: false } : item))
    : layers;
  const effectiveLayers = draft
    ? rebuildLayerTree(shownLayers, draft.placements, draft.folderOpenById)
    : shownLayers;

  // Folders can't be nested, so every folder is a root item.
  const folderOpenById: FolderOpenById = new Map(
    effectiveLayers
      .filter((item) => item.type === "folder")
      .map((folder) => [folder.id, folder.opened])
  );

  const rows = buildRenderRows(effectiveLayers);

  const handleDragStart = (draggedId: string) => {
    setIsDraggingFolder(folderOpenById.has(draggedId));

    // Drag the whole selection only if the dragged row is part of it.
    const selectedIds = getSelectedLayerIds(rows);
    const dragIds =
      selectedIds.length > 1 && selectedIds.includes(draggedId) ? selectedIds : [draggedId];

    const dragIdSet = new Set(dragIds);
    sessionRef.current = {
      dragIds,
      otherIds:
        dragIds.length > 1 ? buildFlatOrder(layers).filter((id) => !dragIdSet.has(id)) : null,
    };
  };

  function highlightMap(map: HTMLElement | null) {
    if (highlightedMapRef.current === map) return;
    if (highlightedMapRef.current) highlightedMapRef.current.style.borderColor = "";
    if (map) map.style.borderColor = dragOverColor;
    highlightedMapRef.current = map;
  }

  // Runs on every pointer move. Highlights the map under the pointer, and for multi-row drags
  // moves the dragged rows to where the pointer is.
  const handleDrag = (_event: DragEvent, info: PanInfo) => {
    highlightMap(getMapElementAtPoint(info.point.x, info.point.y));

    const session = sessionRef.current;
    const container = containerRef.current;
    // Single rows are handled by framer-motion, see `handleReorder`.
    if (!session?.otherIds || !container) return;

    const insertIndex = findDropIndex(
      container,
      info.point.y,
      new Set(session.dragIds),
      session.otherIds
    );
    const newIds = insertBlockAt(session.otherIds, session.dragIds, insertIndex);

    // Only update when the order changed, as updating on every pointer move can cause "Maximum
    // update depth exceeded".
    if (hasSameOrder(newIds, buildFlatOrder(effectiveLayers))) return;

    setDraft({ placements: idsToPlacements(newIds, folderOpenById), folderOpenById });
  };

  // Called by framer-motion when a single row is dragged past another row.
  const handleReorder = (newIds: string[]) => {
    if (sessionRef.current?.otherIds) return;

    setDraft({ placements: idsToPlacements(newIds, folderOpenById), folderOpenById });
  };

  const handleDragEnd = (_event: DragEvent, info: PanInfo) => {
    const dragIds = sessionRef.current?.dragIds ?? [];
    const dropMapId = getMapElementAtPoint(info.point.x, info.point.y)?.dataset.mapId;

    if (dropMapId && dropMapId !== openMapId) {
      moveLayersToMap(dragIds, dropMapId);
    } else if (draft) {
      reorderFlatLayers(draft.placements, draft.folderOpenById);
    }

    highlightMap(null);
    setIsDraggingFolder(false);
    setDraft(null);
    sessionRef.current = null;
  };

  return {
    effectiveLayers,
    rows,
    containerRef,
    handleDragStart,
    handleDrag,
    handleDragEnd,
    handleReorder,
  };
}
